import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../servicios/firebase';
import { useAuth } from '../../admin/context/AuthContext';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import BotonVolver from '../../admin/components/ui/BotonVolver';

const COLORES = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const ETIQUETAS_CANAL = {
  taquilla: '🏪 Taquilla',
  telefono: '📞 Telefónica',
  whatsapp: '📱 WhatsApp',
  otro: '🌐 Otro',
};

// Modal de desglose de boletos
const ModalDesglose = ({ titulo, boletos, onClose }) => {
  const [orden, setOrden] = useState('recientes');
  if (!boletos || boletos.length === 0) return null;

  const boletosOrdenados = [...boletos].sort((a, b) => {
    const fechaA = a.fechaCompra?.toDate?.() || new Date(0);
    const fechaB = b.fechaCompra?.toDate?.() || new Date(0);
    return orden === 'recientes' ? fechaB - fechaA : fechaA - fechaB;
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
          <h3 className="font-black text-gray-800">{titulo}</h3>
          <div className="flex items-center gap-3">
            <select
              value={orden}
              onChange={e => setOrden(e.target.value)}
              className="text-xs font-bold text-gray-600 border border-gray-200 rounded-lg px-2 py-1 outline-none bg-white"
            >
              <option value="recientes">🕐 Más recientes primero</option>
              <option value="antiguos">🕰️ Más antiguos primero</option>
            </select>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-xl">✕</button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-2">
          {boletosOrdenados.map((b, idx) => {
            const cancelado = b.estado?.startsWith('cancelado');
            const conReembolso = b.estado === 'cancelado_con_reembolso';
            const sinReembolso = b.estado === 'cancelado_sin_reembolso';
            const montoReembolso = b.reembolso?.monto || 0;

            return (
              <div key={b.id || idx} className={`p-3 mb-2 rounded-xl border text-xs ${cancelado ? 'bg-red-50 border-red-100' : 'bg-white border-gray-100'}`}>
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${b.tipoViaje === 'redondo' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {b.tipoViaje === 'redondo' ? '⮂ REDONDO' : '→ SENCILLO'}
                    </span>
                    {cancelado && (
                      <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${conReembolso ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                        {conReembolso ? `💵 Reembolsado $${montoReembolso}` : '🔓 Liberado sin reembolso'}
                      </span>
                    )}
                  </div>
                  <span className="font-black text-emerald-600">${b.precioTotal || 0}</span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-gray-600">
                  <p><span className="font-bold text-gray-400">Pasajero:</span> {b.contacto?.nombre || '—'}</p>
                  <p><span className="font-bold text-gray-400">Tel:</span> {b.contacto?.telefono || '—'}</p>
                  <p><span className="font-bold text-gray-400">Ruta:</span> {b.ruta?.origen || b.rutaIda?.origen || '—'} → {b.ruta?.destino || b.rutaIda?.destino || '—'}</p>
                  <p><span className="font-bold text-gray-400">Fecha:</span> {b.fechaViaje || b.fechaIda || '—'}</p>
                  <p><span className="font-bold text-gray-400">Hora:</span> {b.horaViaje || b.horaIda || '—'}</p>
                  <p><span className="font-bold text-gray-400">Asientos:</span> {(b.asientos || b.asientosIda || []).join(', ') || '—'}</p>
                  <p><span className="font-bold text-gray-400">Canal:</span> {ETIQUETAS_CANAL[b.canalVenta] || b.canalVenta || '—'}</p>
                  <p><span className="font-bold text-gray-400">Atendió:</span> {b.atendidoPor || '—'}</p>
                  {cancelado && (
                    <>
                      <p><span className="font-bold text-gray-400">Motivo:</span> {b.motivoCancelacion || '—'}</p>
                      <p><span className="font-bold text-gray-400">Canceló:</span> {b.canceladoPor || '—'}</p>
                    </>
                  )}
                  {conReembolso && (
                    <>
                      <p><span className="font-bold text-gray-400">Tipo reembolso:</span> {b.reembolso?.tipo === 'parcial' ? 'Parcial' : 'Total'}</p>
                      <p><span className="font-bold text-gray-400">Autorizó:</span> {b.reembolso?.por || '—'}</p>
                    </>
                  )}
                  <p><span className="font-bold text-gray-400">Folio:</span> {b.id || '—'}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t bg-gray-50 rounded-b-2xl text-xs text-gray-500 text-center">
          {boletos.length} boleto(s) •{' '}
          Total cobrado: <span className="font-black text-emerald-600">${boletos.reduce((s, b) => s + (b.precioTotal || 0), 0).toLocaleString()}</span>
          {boletos.some(b => b.reembolso?.monto > 0) && (
            <> • Reembolsado: <span className="font-black text-blue-600">${boletos.reduce((s, b) => s + (b.reembolso?.monto || 0), 0).toLocaleString()}</span></>
          )}
        </div>
      </div>
    </div>
  );
};

const Reportes = () => {
  const { user } = useAuth();
  const [datosUsuario, setDatosUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('dia');
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);

  // Modal de desglose
  const [modalDesglose, setModalDesglose] = useState(null); // { titulo, boletos }

  const [stats, setStats] = useState({
    totalVentas: 0,
    totalIngresos: 0,
    asientosVendidos: 0,
    ocupacionPromedio: 0,
    ventasPorHora: [],
    ventasPorDia: [],
    rutasMasVendidas: [],
    dineroFisico: 0,
    dineroDigital: 0,
    boletosFisicos: 0,
    boletosDigitales: 0,
    totalReembolsos: 0,
    // Listas para modal
    listaBoletos: [],
    listaBoletosFisicos: [],
    listaBoletosDigitales: [],
  });

  useEffect(() => {
    const cargarUsuario = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'usuarios', user.uid));
        if (userDoc.exists()) setDatosUsuario(userDoc.data());
        else setDatosUsuario(user);
      } catch (error) { console.error("Error:", error); }
    };
    cargarUsuario();
  }, [user]);

  const calcularRangoFechas = () => {
    const hoy = new Date(fechaInicio);
    let inicio = new Date(hoy);
    let fin = new Date(hoy);
    if (periodo === 'semana') inicio.setDate(inicio.getDate() - 6);
    else if (periodo === 'mes') inicio.setDate(inicio.getDate() - 29);
    return {
      inicio: inicio.toISOString().split('T')[0],
      fin: fin.toISOString().split('T')[0]
    };
  };

  useEffect(() => {
    if (!datosUsuario) return;

    const cargarEstadisticas = async () => {
      setLoading(true);
      try {
        const { inicio, fin } = calcularRangoFechas();

        const qTickets = query(
          collection(db, 'tickets'),
          where('fechaCompra', '>=', Timestamp.fromDate(new Date(inicio))),
          where('fechaCompra', '<=', Timestamp.fromDate(new Date(fin + 'T23:59:59')))
        );

        const querySnapshot = await getDocs(qTickets);

        let dFisico = 0, dDigital = 0;
        let bFisicos = 0, bDigitales = 0;
        let totalReembolsos = 0;
        let ventasPorHoraMap = {};
        let ventasPorDiaMap = {};
        let rutasMap = {};
        let listaBoletos = [];
        let listaBoletosFisicos = [];
        let listaBoletosDigitales = [];

        querySnapshot.forEach((docSnap) => {
          const data = { id: docSnap.id, ...docSnap.data() };

          // Filtro por sucursal
          if (datosUsuario.rol === 'admin_sucursal' && data.ruta?.origen !== datosUsuario.sucursal) return;

          const monto = data.precioTotal || 0;
          const numAsientos = (data.asientos?.length || 0) || (data.asientosIda?.length || 0) || 1;
          const esFisico = data.metodoVenta === 'fisica' || data.metodoPago === 'efectivo_taquilla';
          const reembolso = data.reembolso?.monto || 0;

          // Acumular reembolsos para descontar de totales
          totalReembolsos += reembolso;

          // Solo contar ingresos si no fue cancelado con reembolso total
          const montoEfectivo = data.estado === 'cancelado_con_reembolso'
            ? Math.max(0, monto - reembolso)
            : data.estado === 'cancelado_sin_reembolso'
              ? monto // se conserva el ingreso
              : monto;

          if (esFisico) {
            dFisico += montoEfectivo;
            bFisicos += numAsientos;
            listaBoletosFisicos.push(data);
          } else {
            dDigital += montoEfectivo;
            bDigitales += numAsientos;
            listaBoletosDigitales.push(data);
          }
          listaBoletos.push(data);

          // Solo incluir en gráficas si está pagado (no cancelado)
          if (data.estado === 'pagado') {
            const hora = data.horaViaje || data.horaIda || '00:00';
            ventasPorHoraMap[hora] = (ventasPorHoraMap[hora] || 0) + numAsientos;

            const fecha = data.fechaViaje || data.fechaIda || 'S/F';
            ventasPorDiaMap[fecha] = (ventasPorDiaMap[fecha] || 0) + numAsientos;

            const nombreRuta = data.ruta
              ? `${data.ruta.origen} → ${data.ruta.destino}`
              : data.rutaIda
                ? `${data.rutaIda.origen} → ${data.rutaIda.destino}`
                : 'Desconocida';
            rutasMap[nombreRuta] = (rutasMap[nombreRuta] || 0) + numAsientos;
          }
        });

        // Ordenar listas: cancelados al final
        const ordenarBoletos = (lista) => lista.sort((a, b) => {
          if (a.estado === 'pagado' && b.estado !== 'pagado') return -1;
          if (a.estado !== 'pagado' && b.estado === 'pagado') return 1;
          return 0;
        });

        const ventasPorHora = Object.entries(ventasPorHoraMap)
          .map(([hora, ventas]) => ({ hora, ventas }))
          .sort((a, b) => a.hora.localeCompare(b.hora));

        const ventasPorDia = Object.entries(ventasPorDiaMap)
          .map(([fecha, ventas]) => ({ fecha: fecha.slice(5), ventas }))
          .sort((a, b) => a.fecha.localeCompare(b.fecha));

        const rutasMasVendidas = Object.entries(rutasMap)
          .map(([ruta, ventas]) => ({ ruta, ventas }))
          .sort((a, b) => b.ventas - a.ventas)
          .slice(0, 5);

        setStats({
          asientosVendidos: bFisicos + bDigitales,
          totalIngresos: dFisico + dDigital,
          dineroFisico: dFisico,
          dineroDigital: dDigital,
          boletosFisicos: bFisicos,
          boletosDigitales: bDigitales,
          totalReembolsos,
          ventasPorHora,
          ventasPorDia,
          rutasMasVendidas,
          ocupacionPromedio: (((bFisicos + bDigitales) / 16) * 100).toFixed(1),
          listaBoletos: ordenarBoletos(listaBoletos),
          listaBoletosFisicos: ordenarBoletos(listaBoletosFisicos),
          listaBoletosDigitales: ordenarBoletos(listaBoletosDigitales),
        });

      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarEstadisticas();
  }, [datosUsuario, periodo, fechaInicio]);

  if (!datosUsuario) return <div className="p-10 text-center">Cargando perfil...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">

      {/* Modal de desglose */}
      {modalDesglose && (
        <ModalDesglose
          titulo={modalDesglose.titulo}
          boletos={modalDesglose.boletos}
          onClose={() => setModalDesglose(null)}
        />
      )}

      <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">📊 Inteligencia de Negocio</h1>
          <p className="text-gray-500 text-sm">
            {datosUsuario.rol === 'super_admin' ? 'Vista Global' : `Vista: ${datosUsuario.sucursal}`}
          </p>
        </div>
        <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-xl border">
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="bg-transparent font-bold text-gray-700 outline-none">
            <option value="dia">Hoy</option>
            <option value="semana">Últimos 7 Días</option>
            <option value="mes">Últimos 30 Días</option>
          </select>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="bg-transparent font-bold text-gray-700 outline-none" />
        </div>
        <BotonVolver />
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400 animate-pulse">Analizando datos...</div>
      ) : (
        <>
          {/* DIFERENCIACIÓN VISUAL — clic para ver desglose */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <button
              onClick={() => setModalDesglose({ titulo: '🏪 Efectivo Taquilla — Desglose', boletos: stats.listaBoletosFisicos })}
              className="bg-white p-6 rounded-2xl shadow-sm border-l-8 border-blue-500 text-left hover:shadow-md transition-shadow group"
            >
              <div className="text-xs font-bold text-gray-400 uppercase">Efectivo Taquilla (Físico)</div>
              <div className="text-3xl font-black text-blue-600">${stats.dineroFisico.toLocaleString()}</div>
              <div className="text-sm text-gray-500">{stats.boletosFisicos} boletos</div>
              <div className="text-[10px] text-blue-400 font-bold mt-2 group-hover:underline">Ver desglose →</div>
            </button>

            <button
              onClick={() => setModalDesglose({ titulo: '💳 Ventas Web — Desglose', boletos: stats.listaBoletosDigitales })}
              className="bg-white p-6 rounded-2xl shadow-sm border-l-8 border-purple-500 text-left hover:shadow-md transition-shadow group"
            >
              <div className="text-xs font-bold text-gray-400 uppercase">Ventas Web (Digital)</div>
              <div className="text-3xl font-black text-purple-600">${stats.dineroDigital.toLocaleString()}</div>
              <div className="text-sm text-gray-500">{stats.boletosDigitales} boletos</div>
              <div className="text-[10px] text-purple-400 font-bold mt-2 group-hover:underline">Ver desglose →</div>
            </button>
          </div>

          {/* Reembolsos si los hay */}
          {stats.totalReembolsos > 0 && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 flex justify-between items-center">
              <div>
                <p className="text-xs font-bold text-blue-700 uppercase">💵 Total reembolsado en el periodo</p>
                <p className="text-2xl font-black text-blue-700">-${stats.totalReembolsos.toLocaleString()}</p>
              </div>
              <button
                onClick={() => setModalDesglose({
                  titulo: '💵 Boletos con reembolso',
                  boletos: stats.listaBoletos.filter(b => b.reembolso?.monto > 0)
                })}
                className="text-xs text-blue-600 font-bold hover:underline"
              >
                Ver detalle →
              </button>
            </div>
          )}

          {/* MÉTRICAS */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-xs font-bold text-gray-400 uppercase">Total Boletos</div>
              <div className="text-2xl font-bold">{stats.asientosVendidos}</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-xs font-bold text-gray-400 uppercase">Ingreso Neto</div>
              <div className="text-2xl font-bold text-emerald-600">${(stats.totalIngresos).toLocaleString()}</div>
              {stats.totalReembolsos > 0 && <div className="text-[10px] text-gray-400">Reembolsos descontados: -${stats.totalReembolsos.toLocaleString()}</div>}
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-xs font-bold text-gray-400 uppercase">Ocupación</div>
              <div className="text-2xl font-bold">{stats.ocupacionPromedio}%</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <div className="text-xs font-bold text-gray-400 uppercase">Ticket Promedio</div>
              <div className="text-2xl font-bold">${stats.asientosVendidos > 0 ? Math.round(stats.totalIngresos / stats.asientosVendidos) : 0}</div>
            </div>
          </div>

          {/* GRÁFICAS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border h-96">
              <h3 className="font-bold text-gray-700 mb-4">Ventas por Horario</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.ventasPorHora}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="hora" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border h-96">
              <h3 className="font-bold text-gray-700 mb-4">Rutas Más Populares</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.rutasMasVendidas} dataKey="ventas" nameKey="ruta" cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5}>
                    {stats.rutasMasVendidas.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORES[index % COLORES.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* TABLA DETALLE */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="p-4 bg-gray-50 border-b font-bold text-gray-700">Desglose por Ruta</div>
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-400 uppercase bg-white">
                <tr>
                  <th className="px-6 py-3">Ruta</th>
                  <th className="px-6 py-3 text-right">Boletos</th>
                  <th className="px-6 py-3 text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {stats.rutasMasVendidas.map((ruta, idx) => (
                  <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{ruta.ruta}</td>
                    <td className="px-6 py-4 text-right">{ruta.ventas}</td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      {((ruta.ventas / stats.asientosVendidos) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Reportes;